/**
 * ai/index.ts —— AI 模块公开出口 + 把决策接到牌局引擎上的驱动器
 *
 * ⚠ 本目录下所有文件禁止引用 ../equity 与 ../preflopTable（PRD 2.1 / AC2.5）。
 *   这里只 import ../game 的**类型与只读查询**，用于把引擎状态翻译成 DecisionInput。
 */

export * from './handStrength';
export * from './styles';
export * from './decision';

import type { Game } from '../game';
import type { Action } from '../betting';
import { createAiState, decide, updateEmotion, type AiState, type Decision, type DecisionLog, type Position } from './decision';
import { STYLE_KEYS, type StyleKey } from './styles';
import type { Rng } from '../rng';

/** AI 席位管理器：持有各 AI 的风格与情绪状态，并产出决策与日志。 */
export class AiTable {
  private readonly states = new Map<number, AiState>();
  private readonly logs: DecisionLog[] = [];
  private readonly stackRef: number;

  /**
   * @param seats     AI 占用的座位号
   * @param styles    座位 → 风格；未指定的座位用 aiRNG 随机分配（PRD 模块 3「AI 风格构成」）
   * @param stackRef  情绪判定用的参考筹码量（一般为起始带入筹码）
   * @param rng       aiRNG
   */
  constructor(seats: readonly number[], styles: Partial<Record<number, StyleKey>>, stackRef: number, rng: Rng) {
    this.stackRef = stackRef;
    for (const seat of seats) {
      const style = styles[seat] ?? STYLE_KEYS[rng.nextInt(STYLE_KEYS.length)];
      this.states.set(seat, createAiState(seat, style));
    }
  }

  styleOf(seat: number): StyleKey {
    const s = this.states.get(seat);
    if (!s) throw new Error(`座位 ${seat} 不是 AI`);
    return s.style;
  }

  stateOf(seat: number): AiState {
    const s = this.states.get(seat);
    if (!s) throw new Error(`座位 ${seat} 不是 AI`);
    return s;
  }

  has(seat: number): boolean { return this.states.has(seat); }

  /** 决策日志（AC2.6，可直接序列化为 JSON / CSV 导出）。 */
  decisionLogs(): readonly DecisionLog[] { return this.logs; }

  /** 导出为 CSV（AC2.6）。 */
  toCsv(): string {
    const head = 'seat,style,street,tier,strengthScore,emotion,jitter,roll,mistake,position,pressure,action,amount,thinkMs,reason';
    const rows = this.logs.map((l) => [
      l.seat, l.style, l.street, l.tier, l.strengthScore.toFixed(4), l.emotion,
      l.jitter.toFixed(4), l.roll.toFixed(4), l.mistake, l.position, l.pressure.toFixed(3),
      l.action.type, l.action.amount ?? '', Math.round(l.thinkMs), JSON.stringify(l.reason),
    ].join(','));
    return [head, ...rows].join('\n');
  }

  /**
   * 为当前待行动的 AI 产出一个动作。
   * @param game 牌局（只读取公开状态 + 该 AI 自己的底牌）
   * @param rng  aiRNG
   */
  act(game: Game, seat: number, rng: Rng, speed = 1): Decision {
    const state = this.stateOf(seat);
    const player = game.players[seat];
    if (!player || !player.hole) throw new Error(`座位 ${seat} 没有底牌`);

    const input = {
      hole: player.hole,
      // 只传已公开的公共牌 —— 这是信息隔离的关键（PRD 8.3）
      board: [...game.board],
      pot: game.pot,
      stack: player.stack,
      streetContributed: player.streetContributed,
      legal: game.legalActionsFor(seat),
      bigBlind: game.config.bigBlind,
      currentBet: game.currentBet,
      position: positionOf(game, seat),
      activePlayers: game.activeSeats().length,
      facingRaise: game.board.length === 0
        ? game.currentBet > game.config.bigBlind
        : game.currentBet > 0,
      myPriorActions: game.currentEvents()
        .filter((e): e is Extract<typeof e, { kind: 'action' }> => e.kind === 'action' && e.effect.seat === seat)
        .map((e) => e.effect.type),
    };

    const d = decide(state, input, rng, speed);
    this.logs.push(d.log);
    return d;
  }

  /** 每手结束后调用，更新所有 AI 的情绪（PRD 2.1 ②）。 */
  endHand(net: ReadonlyMap<number, number>, rng: Rng): void {
    for (const [seat, state] of this.states) {
      updateEmotion(state, net.get(seat) ?? 0, this.stackRef, rng);
    }
  }
}

/** 位置分类：按「距离按钮多远」划分（PRD 2.1 决策输入之一）。 */
export function positionOf(game: Game, seat: number): Position {
  const seats = game.activeSeats();
  const n = game.config.seatCount;
  const inHand = game.players.filter((p) => p && p.inHand).map((p) => p!.seat);
  if (inHand.length <= 2) {
    // 单挑：按钮位即后位
    return seat === game.buttonSeat ? 'late' : 'blinds';
  }
  // 按钮左侧第 1、2 位是盲注位
  const sb = nextIn(inHand, game.buttonSeat, n);
  const bb = nextIn(inHand, sb, n);
  if (seat === sb || seat === bb) return 'blinds';

  // 其余按「从大盲左侧起的相对次序」三等分
  const order: number[] = [];
  let cur = bb;
  for (let i = 0; i < inHand.length; i++) {
    cur = nextIn(inHand, cur, n);
    if (cur === sb) break;
    order.push(cur);
  }
  const idx = order.indexOf(seat);
  if (idx < 0) return 'middle';
  const third = order.length / 3;
  void seats;
  return idx < third ? 'early' : idx < third * 2 ? 'middle' : 'late';
}

function nextIn(inHand: readonly number[], from: number, seatCount: number): number {
  for (let i = 1; i <= seatCount; i++) {
    const s = (from + i) % seatCount;
    if (inHand.includes(s)) return s;
  }
  return from;
}

/** 便捷函数：让 AI 直接产出动作（不需要日志时用）。 */
export function aiAction(table: AiTable, game: Game, seat: number, rng: Rng, speed = 1): Action {
  return table.act(game, seat, rng, speed).action;
}
